import i18n from '@/utils/i18n';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useMemo } from 'react';
import { addToOrderPrebookItemDisabled } from '@/contexts/item-context-utils';
import { Button } from './button';
import {
  CATALOG_ITEM_CARD_MARGIN,
  CATALOG_ITEM_CARD_PADDING,
} from './catalog-item-card';
import { ChatStatus } from '@/enums';
import { createStyles } from '@/contexts/theme-context';
import {
  EXPANDED_QUANTITY_WIDTH,
  ORDER_ITEM_QUANTITY_HEIGHT,
} from './quantity-selector';
import { ItemListOption } from '@/contexts/item-context-types';
import { ItemSubtitleV2 } from './item-subtitle-v2';
import { MinimalOrderableItemActionButton } from './orderable-item-action-button';
import { MinimalOrderableItemCardV2 } from './minimal-orderable-item-card-v2';
import { PixelRatio, StyleProp, View, ViewStyle } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import {
  useAddToOrderDisabled,
  useAvailabilityStatusEnabled,
} from '@/hooks/use-add-to-order-disabled';
import { useItemLabels, useRenderedLabels } from '@/hooks/use-item-labels';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { useVolumePromotionsContext } from '@/contexts/volume-promotions-context';

const MINIMAL_ORDERABLE_ITEM_WIDTH =
  EXPANDED_QUANTITY_WIDTH +
  2 * (CATALOG_ITEM_CARD_MARGIN + CATALOG_ITEM_CARD_PADDING);

interface MinimalOrderableItemListItemV2Props {
  item: ItemListOption;
  onSelect: () => void;
  size?: 'small' | 'large';
  style?: StyleProp<ViewStyle>;
  updateQuantity: UpdateQuantityFn;
  width?: number;
}

const fontScale = PixelRatio.getFontScale();
const ICON_SIZE = 16 * fontScale;

export const MinimalOrderableItemListItemV2: React.FC<
  MinimalOrderableItemListItemV2Props
> = ({ item, onSelect, size = 'small', style, updateQuantity, width }) => {
  const [styles] = useStyles();
  const metadata = item.metadata!;
  const badges = metadata.badges;
  const { essentialVariantPackUUIDs } = useVariantPackInfoContext();
  const { supplierTimezone } = useNewOrderContext();
  const { variantPackPromotionsMap } = useVolumePromotionsContext();
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();
  const {
    selectedRestaurantRelationship: { chatStatus },
  } = useUserContext();

  const isProspect = chatStatus === ChatStatus.PROSPECT;
  const variantPackUUID = metadata.variantPackUUID;
  const isAddToOrderDisabled = useAddToOrderDisabled(variantPackUUID);
  const quantity = useItemVisibleQuantity(variantPackUUID);

  const variantPackPromotions = useMemo(
    () => variantPackPromotionsMap?.[variantPackUUID] ?? [],
    [variantPackPromotionsMap, variantPackUUID],
  );

  const promotionBadgeLabel = useMemo(() => {
    const visiblePromotions = variantPackPromotions.filter(
      (promotion) => !promotion.hideTagline,
    );
    const metadataPromotionTagline =
      metadata.promotion && !metadata.promotion.hideTagline
        ? metadata.promotion.tagline
        : null;
    const promotionCount = visiblePromotions.length;
    if (promotionCount === 1) {
      const [promotion] = visiblePromotions;
      if (promotion?.tagline && promotion.tagline.trim().length > 0) {
        return promotion.tagline;
      }
      if (metadataPromotionTagline) {
        return metadataPromotionTagline;
      }
      return null;
    }

    if (promotionCount > 1) {
      return `${promotionCount} ${i18n.t('promotions_lower_cased')}`;
    }

    return metadataPromotionTagline;
  }, [metadata.promotion, variantPackPromotions]);

  const isEssential = essentialVariantPackUUIDs.has(variantPackUUID);
  const renderAsSmall = size === 'small';
  const renderAsLarge = size === 'large';

  const isAddToOrderPrebookItemDisabled = useMemo(
    () => addToOrderPrebookItemDisabled(metadata, supplierTimezone),
    [metadata, supplierTimezone],
  );

  const itemLabels = useItemLabels(metadata, false, renderAsSmall);

  const renderedLabels = useRenderedLabels(itemLabels);

  const subtitle = (
    <ItemSubtitleV2
      currencyCode={metadata.currencyCode}
      displayString={metadata.packDisplayString}
      promotionPriceInMicros={metadata.promotionPricePerUnitInMicros}
      sku={metadata.externalID}
      style={styles.subtitle}
      unitCount={metadata.unitCount}
      unitPriceInMicros={metadata.unitPriceInMicros}
    />
  );

  const actionButton = (
    <MinimalOrderableItemActionButton
      availabilityStatusEnabled={availabilityStatusEnabled}
      isAddToOrderDisabled={isAddToOrderDisabled}
      isAddToOrderPrebookItemDisabled={isAddToOrderPrebookItemDisabled}
      isEssential={isEssential}
      isProspect={isProspect}
      metadata={metadata}
      onSelect={onSelect}
      quantity={quantity}
      quantityPillStyle={styles.quantityPill}
      quantitySelectorProps={{
        variant: 'blurred',
      }}
      renderAsSmall={renderAsSmall}
      renderMultiSkuButton={({
        onSelect: handleSelect,
        variantPackUUID: multiSkuVariantPackUUID,
      }) => (
        <Button
          Icon={PlusIcon}
          iconProps={{
            height: ICON_SIZE,
            width: ICON_SIZE,
          }}
          onPress={handleSelect}
          rounded
          size="small"
          style={styles.seeOptionButtonV2}
          trackingData={{
            name: 'orderable_catalog_item.see_options',
            properties: {
              variantPackUUID: multiSkuVariantPackUUID,
            },
          }}
        />
      )}
      unavailableTextStyle={[TYPOGRAPHY.pSemiBold, styles.unavailableText]}
      unavailableWrapperStyle={styles.unavailableWrapper}
      updateQuantity={updateQuantity}
      variantPackUUID={variantPackUUID}
    />
  );

  const altText = [metadata.displayName, metadata.externalID]
    .filter(Boolean)
    .join('-');

  const actionElement = (
    <View
      style={
        renderAsSmall
          ? styles.quantityWrapperSmall
          : styles.quantityWrapperLarge
      }
    >
      {actionButton}
    </View>
  );

  const cardV2Style: StyleProp<ViewStyle> = [
    styles.cardV2Base,
    styles.base,
    style,
  ];

  return (
    <MinimalOrderableItemCardV2
      actionElement={actionElement}
      badges={badges}
      description={metadata.description}
      imageAlt={altText}
      imageRecyclingKey={metadata.photoURLs[0]}
      imageUri={metadata.photoURLs[0]}
      onPress={onSelect}
      promotionBadgeLabel={promotionBadgeLabel}
      renderAsLarge={renderAsLarge}
      style={cardV2Style}
      subtitle={
        <>
          <View>{subtitle}</View>
          <View style={styles.labelsRow}>{renderedLabels}</View>
        </>
      }
      title={item.title}
      width={width}
    />
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    width: MINIMAL_ORDERABLE_ITEM_WIDTH,
  },
  cardV2Base: {
    margin: CATALOG_ITEM_CARD_MARGIN,
  },
  labelsRow: {
    flexWrap: 'wrap',
    marginTop: 8,
  },
  quantityPill: {
    maxWidth: 72,
  },
  quantityWrapperLarge: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 4,
    width: '100%',
  },
  quantityWrapperSmall: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  seeOptionButtonV2: {
    backgroundColor: theme.colors.accent,
    height: ORDER_ITEM_QUANTITY_HEIGHT,
  },
  subtitle: {
    justifyContent: 'flex-start',
  },
  unavailableText: {
    color: theme.colors.inactiveText,
  },
  unavailableWrapper: {
    height: ORDER_ITEM_QUANTITY_HEIGHT,
    justifyContent: 'center',
  },
}));
